import { useApiWithAuth } from "@/modules/api";
import { PermissionPayload } from "@/modules/auth";

type TipoUsuario = "R" | "A" | "F" | "C";

interface ClienteUsuarioPayload {
  idUsuario?: number | null;
  login?: string | null;
  nome?: string | null;
  senha?: string | null;
  tipo?: TipoUsuario | null;
  ativo?: boolean | null;
  dataUltimoLogin?: Date | null;
  permissionInfo?: PermissionPayload;
}

interface TipoUsuarioOption {
  tipo: TipoUsuario;
  descricao: string;
}

const TIPOS_USUARIO: Array<TipoUsuarioOption> = [
  { tipo: "R", descricao: "R - GovPass" },
  { tipo: "A", descricao: "A - Administrador" },
  { tipo: "F", descricao: "F - Funcionário" },
  { tipo: "C", descricao: "C - Transportador" }
];

/** Logins de uso exclusivo da GovPass: autenticam com a senha do usuário root. */
const PREFIXO_LOGIN_GOVPASS = "root-";

/**
 * Usuários de um cliente. Com idCliente usa a rota do root (qualquer cliente);
 * sem idCliente, a do administrador (o próprio cliente).
 */
const useClienteUsuarioService = () => {
  const { data, loading, get, post, put } = useApiWithAuth();

  const getPath = (idCliente?: number | null) => {
    return idCliente
      ? `/root/cliente/${idCliente}/usuarios`
      : "/admin/cliente/usuarios";
  };

  const getUsuarios = (idCliente?: number | null): Promise<void> => {
    return get(getPath(idCliente));
  };

  const saveUsuario = (
    idCliente: number | null | undefined,
    usuario: ClienteUsuarioPayload
  ): Promise<void> => {
    return usuario.idUsuario
      ? put(`${getPath(idCliente)}/${usuario.idUsuario}`, usuario)
      : post(getPath(idCliente), usuario);
  };

  const getDescricaoTipo = (tipo?: TipoUsuario | null): string => {
    return TIPOS_USUARIO.find((t) => t.tipo === tipo)?.descricao ?? "";
  };

  const isLoginGovpass = (login?: string | null): boolean => {
    return (login ?? "").toLowerCase().startsWith(PREFIXO_LOGIN_GOVPASS);
  };

  return {
    data,
    loading,
    getUsuarios,
    saveUsuario,
    getDescricaoTipo,
    isLoginGovpass
  };
};

export {
  useClienteUsuarioService,
  ClienteUsuarioPayload,
  TipoUsuario,
  TipoUsuarioOption,
  TIPOS_USUARIO
};
